import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../_metronic/layout/core'
import { KTCard, KTCardBody } from '../../../_metronic/helpers'
import { KTIcon } from '../../../_metronic/helpers'
import { create_platform } from './api/platformsApi'
import { toast } from 'react-toastify'
import { permissionCheck } from '../../helper-functions/PermissionCheck'
import { validateImage } from '../../helper-functions/ImageValidation'

const AddPlatformPage: React.FC = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    platform_name: '',
    platform_url: '',
    description: '',
    status: 1
  })
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const canCreatePlatform = Boolean(permissionCheck('platform-create'))

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.platform_name.trim()) {
      newErrors.platform_name = 'Platform name is required'
    }

    if (!formData.platform_url.trim()) {
      newErrors.platform_url = 'Platform URL is required'
    } else {
      try {
        new URL(formData.platform_url)
      } catch {
        newErrors.platform_url = 'Please enter a valid URL'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!canCreatePlatform) {
      toast.error('You do not have permission to create platforms')
      return
    }

    if (validateForm()) {
      try {
        setIsSubmitting(true)
        const formDataObj = new FormData()
        formDataObj.append('platform_name', formData.platform_name.trim())
        formDataObj.append('platform_url', formData.platform_url.trim())
        formDataObj.append('description', formData.description.trim())
        formDataObj.append('status', String(formData.status))
        
        if (logoFile) {
          formDataObj.append('platform_logo', logoFile)
        }
        
        await create_platform(formDataObj)
        toast.success('Platform created successfully')
        navigate('/platforms')
      } catch (error) {
        console.error('Error creating platform:', error)
        toast.error('Failed to create platform')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    logoType: string,
    MAX_FILE_SIZE: number,
    ALLOWED_TYPES: string[],
    MAX_DIMENSIONS: { [key: string]: { width: number; height: number } }
  ) => {
    const newFile = e.target.files?.[0]
    if (newFile) {
      validateImage(
        newFile,
        logoType,
        (validFileUrl) => {
          setLogoFile(newFile)
          setLogoPreview(validFileUrl)
        },
        MAX_FILE_SIZE,
        ALLOWED_TYPES,
        MAX_DIMENSIONS
      )
    }
  }

  const MAX_FILE_SIZE = 2 // MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
  const MAX_DIMENSIONS = { platform_logo: { width: 500, height: 500 } }

  if (!canCreatePlatform) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Add Platform</PageTitle>
        <div className='d-flex flex-column flex-lg-row'>
          <div className='flex-lg-row-fluid'>
            <KTCard>
              <KTCardBody>
                <div className='alert alert-warning mb-0'>
                  You do not have permission to create platforms.
                </div>
              </KTCardBody>
            </KTCard>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Add Platform</PageTitle>
      <div className='d-flex flex-column flex-lg-row'>
        <div className='flex-lg-row-fluid'>
          <KTCard>
          <div className='card-header'>
            <div className='card-title'>
              <h3 className='fw-bold text-dark'>Add New Platform</h3>
            </div>
            <div className='card-toolbar'>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => navigate('/platforms')}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </button>
            </div>
          </div>
          <KTCardBody>
            <form onSubmit={handleSubmit}>
              <div className='row mb-6'>
                <div className='col-lg-6'>
                  <label className='col-form-label fw-bold fs-6'>
                    <span className='text-danger'>*</span>Platform Name
                  </label>
                  <input
                    type='text'
                    name='platform_name'
                    className={`form-control form-control-solid ${errors.platform_name ? 'is-invalid' : ''}`}
                    placeholder='Enter platform name'
                    value={formData.platform_name}
                    onChange={handleChange}
                  />
                  {errors.platform_name && <div className='invalid-feedback'>{errors.platform_name}</div>}
                </div>
                <div className='col-lg-6'>
                  <label className='col-form-label fw-bold fs-6'>Status</label>
                  <select
                    name='status'
                    className='form-select form-select-solid'
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-lg-12'>
                  <label className='col-form-label fw-bold fs-6'>
                    <span className='text-danger'>*</span>Platform URL
                  </label>
                  <input
                    type='url'
                    name='platform_url'
                    className={`form-control form-control-solid ${errors.platform_url ? 'is-invalid' : ''}`}
                    placeholder='https://example.com'
                    value={formData.platform_url}
                    onChange={handleChange}
                  />
                  {errors.platform_url && <div className='invalid-feedback'>{errors.platform_url}</div>}
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-lg-12'>
                  <label className='col-form-label fw-bold fs-6'>Platform Logo</label>
                  <div className='d-flex align-items-center gap-3'>
                    {logoPreview && (
                      <div className='symbol symbol-100px'>
                        <img src={logoPreview} alt='Logo preview' className='w-100' />
                      </div>
                    )}
                    <div className='flex-grow-1'>
                      <input
                        type='file'
                        accept='image/jpeg,image/jpg,image/png,image/gif'
                        className='form-control form-control-solid'
                        onChange={(e) => handleImageChange(e, 'platform_logo', MAX_FILE_SIZE, ALLOWED_TYPES, MAX_DIMENSIONS)}
                      />
                      <div className='form-text'>Max size: 2MB, Max dimensions: 500x500px</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-lg-12'>
                  <label className='col-form-label fw-bold fs-6'>
                    Description
                  </label>
                  <textarea
                    name='description'
                    className={`form-control form-control-solid ${errors.description ? 'is-invalid' : ''}`}
                    rows={3}
                    placeholder='Enter platform description'
                    value={formData.description}
                    onChange={handleChange}
                  />
                  {errors.description && <div className='invalid-feedback'>{errors.description}</div>}
                </div>
              </div>


              <div className='d-flex justify-content-end'>
                <button
                  type='button'
                  className='btn btn-light me-3'
                  onClick={() => navigate('/platforms')}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <KTIcon iconName='check' className='fs-2' />
                      Save Platform
                    </>
                  )}
                </button>
              </div>
            </form>
          </KTCardBody>
        </KTCard>
        </div>
      </div>
    </>
  )
}

export { AddPlatformPage }

